import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  leagueApi,
  LeagueDetails,
  rosterApi,
  Roster,
  RosterWrestler,
  commissionerMessageApi,
  CommissionerMessage,
} from '../services/api-client';
import { Modal } from '../components/Modal';
import { useAuthContext } from '../contexts/AuthContext';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PullToRefresh } from '../components/layout/PullToRefresh';
import { ChatSlideout } from '../components/chat/ChatSlideout';
import { useUnreadCount } from '../hooks/useUnreadCount';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { formatDateTimeWithZone, getUserTimezone } from '../utils/dateTime';
import { ShareButton } from '../components/ShareButton';
import { CommissionerMessageModal } from '../components/CommissionerMessageModal';
import QRCode from 'react-qr-code';

const MODAL_WIN_TYPE_LEGEND: { type: string; description: string; bonus: string }[] = [
  { type: 'Decision', description: 'Win by 1-7 points', bonus: 'No bonus' },
  {
    type: 'Sudden Victory / Tie Breaker',
    description: 'Overtime win after a tied match',
    bonus: 'No bonus',
  },
  { type: 'Major Decision', description: 'Win by 8-14 points', bonus: '+1.0 bonus' },
  { type: 'Tech Fall', description: 'Win by 15+ points (match stopped)', bonus: '+1.5 bonus' },
  {
    type: 'Fall (Pin)',
    description: "Win by pinning opponent's shoulders to the mat",
    bonus: '+2.0 bonus',
  },
  {
    type: 'Forfeit / Default / DQ',
    description: 'Win without completing the match',
    bonus: '+2.0 bonus',
  },
];

const MODAL_SCORING_RULES = [
  { label: 'Championship bracket win', value: '1.0 pt advancement + bonus' },
  { label: 'Consolation bracket win', value: '0.5 pt advancement + bonus' },
  { label: 'Bye + win (championship)', value: '1.0 pt advancement (for the bye)' },
  { label: 'Bye + win (consolation)', value: '0.5 pt advancement (for the bye)' },
  { label: 'Placement match win', value: 'Bonus only (no advancement)' },
];

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function ScoringDetailsModal({
  wrestler,
  onClose,
}: {
  wrestler: RosterWrestler;
  onClose: () => void;
}) {
  const [showLegend, setShowLegend] = useState(false);
  const mr = wrestler.wrestlerMatchResults!;

  return (
    <Modal isOpen={true} onClose={onClose} title={`${wrestler.wrestlerName} — Scoring`}>
      <div className="space-y-4">
        {/* Points summary */}
        <div className="flex gap-4">
          <div className="bg-purple-50 rounded-lg px-4 py-2.5 border border-purple-200 flex-1 text-center">
            <div className="text-xs text-purple-600 uppercase tracking-wide">
              Placing{wrestler.wrestlerPlace ? ` (${ordinal(wrestler.wrestlerPlace)})` : ''}
            </div>
            <div className="text-xl font-bold text-purple-700">{mr.placingPoints}</div>
          </div>
          <div className="bg-blue-50 rounded-lg px-4 py-2.5 border border-blue-200 flex-1 text-center">
            <div className="text-xs text-blue-600 uppercase tracking-wide">Match Pts</div>
            <div className="text-xl font-bold text-blue-700">{mr.matchPointsTotal}</div>
          </div>
          <div className="bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
            <div className="text-xl font-bold text-gray-900">{wrestler.wrestlerTeamPoints}</div>
          </div>
        </div>

        {/* Match list */}
        <div className="space-y-1.5">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Match Results
          </h4>
          {mr.matches.map((match, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${
                match.won ? 'bg-green-50' : 'bg-red-50'
              }`}
            >
              <span
                className={`font-bold text-xs w-5 text-center ${
                  match.won ? 'text-green-700' : 'text-red-600'
                }`}
              >
                {match.won ? 'W' : 'L'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-gray-500 text-xs">{match.round}</div>
                <div className="text-gray-700 text-xs truncate">
                  {match.winType}
                  {match.opponent && (
                    <>
                      {' '}
                      vs {match.opponent}
                      {match.opponentTeam && (
                        <span className="text-gray-400"> ({match.opponentTeam})</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {match.matchPoints > 0 && (
                <span className="text-xs font-semibold text-blue-700 shrink-0">
                  +{match.matchPoints}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Collapsible scoring legend */}
        <div className="border-t border-gray-200 pt-3">
          <button
            onClick={() => setShowLegend(!showLegend)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${showLegend ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
            How does scoring work?
          </button>
          {showLegend && (
            <div className="mt-2 space-y-3 text-xs">
              <div>
                <div className="font-semibold text-gray-700 mb-1">Points per match</div>
                <div className="space-y-0.5">
                  {MODAL_SCORING_RULES.map((rule) => (
                    <div key={rule.label} className="flex gap-2">
                      <span className="text-gray-500 w-44 shrink-0">{rule.label}</span>
                      <span className="text-gray-700">{rule.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">Win types</div>
                <div className="space-y-0.5">
                  {MODAL_WIN_TYPE_LEGEND.map((item) => (
                    <div key={item.type} className="flex gap-2">
                      <span className="text-gray-900 font-medium w-44 shrink-0">{item.type}</span>
                      <span className="text-gray-500">{item.description}</span>
                      <span className="ml-auto text-blue-600 font-medium shrink-0">
                        {item.bonus}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function LeagueDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin: userIsAdmin } = useAuthContext();
  const [league, setLeague] = useState<LeagueDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [memberRosters, setMemberRosters] = useState<Record<string, Roster>>({});
  const [loadingRosters, setLoadingRosters] = useState<Set<string>>(new Set());
  const [editingLeagueName, setEditingLeagueName] = useState(false);
  const [editLeagueName, setEditLeagueName] = useState('');
  const [editingBudget, setEditingBudget] = useState(false);
  const [editBudget, setEditBudget] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [rosterLockDate, setRosterLockDate] = useState('');
  const [rosterLockTime, setRosterLockTime] = useState('');
  const [savingLockSettings, setSavingLockSettings] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [addingCommissioner, setAddingCommissioner] = useState(false);
  const [removingCommissioner, setRemovingCommissioner] = useState<string | null>(null);
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  const [approvingMember, setApprovingMember] = useState<string | null>(null);
  const [rejectingMember, setRejectingMember] = useState<string | null>(null);
  const [requireTeamApproval, setRequireTeamApproval] = useState(false);
  const [allowMultipleEntries, setAllowMultipleEntries] = useState(false);
  const [showDeleteTeamConfirm, setShowDeleteTeamConfirm] = useState(false);
  const [deleteTeamTarget, setDeleteTeamTarget] = useState<{
    id: string;
    teamName: string | null;
  } | null>(null);
  const [deleteTeamConfirmName, setDeleteTeamConfirmName] = useState('');
  const [deletingTeam, setDeletingTeam] = useState(false);

  // Scoring details modal
  const [scoringDetailWrestler, setScoringDetailWrestler] = useState<RosterWrestler | null>(null);

  // Commissioner messages
  const [messageModalTarget, setMessageModalTarget] = useState<{
    memberId: string;
    name: string;
  } | null>(null);
  const [inboxMessages, setInboxMessages] = useState<CommissionerMessage[]>([]);
  const [dismissedMessageIds, setDismissedMessageIds] = useState<Set<string>>(new Set());
  const [showReadMessages, setShowReadMessages] = useState(false);

  // Track unread messages
  const { unreadCount, resetUnreadCount, fetchUnreadCount } = useUnreadCount(id || '');

  // Helper function to format points by removing trailing zeros
  const formatPoints = (points: number): string => {
    // Handle zero explicitly
    if (points === 0) return '0';
    // Convert to string and remove trailing zeros after decimal
    const formatted = points
      .toString()
      .replace(/(\.\d*?)0+$/, '$1')
      .replace(/\.$/, '');
    return formatted;
  };

  useEffect(() => {
    if (id) {
      loadLeague(id);
    }
  }, [id, currentPage]);

  // Fetch commissioner message inbox for current user
  useEffect(() => {
    if (!id) return;
    commissionerMessageApi
      .getInbox(id)
      .then(setInboxMessages)
      .catch(() => {
        // Silently ignore — user may not be a member of this league
      });
  }, [id]);

  // Set approval toggle state from league
  useEffect(() => {
    if (league) {
      setRequireTeamApproval(league.requireTeamApproval ?? false);
    }
  }, [league?.requireTeamApproval]);

  useEffect(() => {
    if (league) {
      setAllowMultipleEntries(league.allowMultipleEntries ?? false);
    }
  }, [league?.allowMultipleEntries]);

  // Auto-expand the current user's team(s) when rosters are unlocked
  // Only expands teams that are visible on the current page
  useEffect(() => {
    if (!league || !user || !id) return;
    if (league.rosterLocked) {
      return;
    }

    // Filter to only user's teams that are on the current page
    const userMembers = league.members.filter((m) => m.cognitoId === user.username);
    if (userMembers.length === 0) {
      return;
    }

    // Check if we've already expanded these specific members
    const newUserMemberIds = userMembers.map((m) => m.id);
    const alreadyHandled = newUserMemberIds.every(
      (id) => expandedMembers.has(id) || memberRosters[id]
    );

    if (alreadyHandled) {
      return;
    }

    // Expand and load rosters for the user's teams on this page
    const userMemberIds = new Set(newUserMemberIds);
    setExpandedMembers(userMemberIds);

    userMembers.forEach(async (member) => {
      if (!memberRosters[member.id]) {
        setLoadingRosters((prev) => new Set(prev).add(member.id));
        try {
          const rosterData = await rosterApi.get(id, member.id);
          setMemberRosters((prev) => ({
            ...prev,
            [member.id]: rosterData,
          }));
        } catch (err) {
          console.error('Failed to auto-load roster:', err);
          setExpandedMembers((prev) => {
            const next = new Set(prev);
            next.delete(member.id);
            return next;
          });
        } finally {
          setLoadingRosters((prev) => {
            const next = new Set(prev);
            next.delete(member.id);
            return next;
          });
        }
      }
    });
  }, [league, user, id, currentPage]);

  const loadLeague = async (leagueId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await leagueApi.get(leagueId, { page: currentPage, pageSize });
      console.log('League data loaded:', {
        id: data.id,
        name: data.name,
        pendingCount: data.pendingCount,
        currentUserTeams: data.currentUserTeams,
        requireTeamApproval: data.requireTeamApproval,
      });
      setLeague(data);
    } catch (err) {
      console.error('Failed to load league:', err);
      setError(err instanceof Error ? err.message : 'Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMember = async (memberId: string) => {
    if (!id) return;
    try {
      setApprovingMember(memberId);
      await leagueApi.approveMember(id, memberId);
      await loadLeague(id);
    } catch (err) {
      console.error('Failed to approve member:', err);
      alert(err instanceof Error ? err.message : 'Failed to approve member');
    } finally {
      setApprovingMember(null);
    }
  };

  const handleRejectMember = async (memberId: string, teamName: string | null) => {
    if (!id) return;
    const confirmMsg = `Are you sure you want to reject ${teamName || 'this team'}? This will delete their account and roster picks.`;
    if (!confirm(confirmMsg)) return;

    try {
      setRejectingMember(memberId);
      await leagueApi.rejectMember(id, memberId);
      await loadLeague(id);
    } catch (err) {
      console.error('Failed to reject member:', err);
      alert(err instanceof Error ? err.message : 'Failed to reject member');
    } finally {
      setRejectingMember(null);
    }
  };

  const handleToggleApprovalRequired = async (enabled: boolean) => {
    if (!id) return;
    try {
      await leagueApi.update(id, { requireTeamApproval: enabled });
      await loadLeague(id);
      setRequireTeamApproval(enabled);
    } catch (err) {
      console.error('Failed to update approval setting:', err);
      alert(err instanceof Error ? err.message : 'Failed to update approval setting');
      // Revert the toggle
      setRequireTeamApproval(!enabled);
    }
  };

  const handleToggleAllowMultipleEntries = async (enabled: boolean) => {
    if (!id) return;
    try {
      await leagueApi.update(id, { allowMultipleEntries: enabled });
      await loadLeague(id);
      setAllowMultipleEntries(enabled);
    } catch (err) {
      console.error('Failed to update multiple entries setting:', err);
      alert(err instanceof Error ? err.message : 'Failed to update multiple entries setting');
      setAllowMultipleEntries(!enabled);
    }
  };

  const copyInviteLink = () => {
    if (league?.inviteCode) {
      const inviteLink = `${window.location.origin}/join/${league.inviteCode}`;
      navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyInviteCode = () => {
    if (league?.inviteCode) {
      navigator.clipboard.writeText(league.inviteCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const startEditTeamName = (memberId: string, currentName: string | null) => {
    setEditingMemberId(memberId);
    setEditTeamName(currentName || '');
  };

  const cancelEditTeamName = () => {
    setEditingMemberId(null);
    setEditTeamName('');
  };

  const saveTeamName = async (memberId: string) => {
    if (!league || !id) return;

    try {
      await leagueApi.updateMember(id, memberId, editTeamName);
      // Reload league to get updated data
      await loadLeague(id);
      setEditingMemberId(null);
      setEditTeamName('');
    } catch (err) {
      console.error('Failed to update team name:', err);
      alert(err instanceof Error ? err.message : 'Failed to update team name');
    }
  };

  const isUserMember = (cognitoId?: string) => {
    if (!user || !cognitoId) return false;
    // Check if this member belongs to the current user by comparing cognitoId
    return cognitoId === user.username;
  };

  const isCommissioner = () => {
    if (!user || !league) return false;
    if (userIsAdmin) return true;
    return league.commissioners?.some((c) => c.cognitoId === user.username) ?? false;
  };

  const startEditLeagueName = () => {
    if (!league) return;
    setEditLeagueName(league.name);
    setEditingLeagueName(true);
  };

  const cancelEditLeagueName = () => {
    setEditingLeagueName(false);
    setEditLeagueName('');
  };

  const saveLeagueName = async () => {
    if (!league || !id || !editLeagueName.trim()) return;

    try {
      await leagueApi.update(id, { name: editLeagueName.trim() });
      await loadLeague(id);
      setEditingLeagueName(false);
      setEditLeagueName('');
    } catch (err) {
      console.error('Failed to update league name:', err);
      alert(err instanceof Error ? err.message : 'Failed to update league name');
    }
  };

  const startEditBudget = () => {
    if (!league) return;
    setEditBudget(league.budgetPerTeam.toString());
    setEditingBudget(true);
  };

  const cancelEditBudget = () => {
    setEditingBudget(false);
    setEditBudget('');
  };

  const saveBudget = async () => {
    if (!league || !id) return;
    const budgetValue = parseInt(editBudget);
    if (isNaN(budgetValue) || budgetValue <= 0) {
      alert('Please enter a valid positive number for budget');
      return;
    }

    try {
      await leagueApi.update(id, { budgetPerTeam: budgetValue });
      await loadLeague(id);
      setEditingBudget(false);
      setEditBudget('');
    } catch (err) {
      console.error('Failed to update budget:', err);
      alert(err instanceof Error ? err.message : 'Failed to update budget');
    }
  };

  const handleDeleteLeague = async () => {
    if (!id) return;

    try {
      setDeleting(true);
      await leagueApi.delete(id);
      navigate('/leagues');
    } catch (err) {
      console.error('Failed to delete league:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete league');
      setDeleting(false);
    }
  };

  const handleDismissMessage = async (messageId: string) => {
    if (!id) return;
    setDismissedMessageIds((prev) => new Set(prev).add(messageId));
    try {
      await commissionerMessageApi.markAsRead(id, messageId);
      setInboxMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, readAt: new Date().toISOString() } : m))
      );
    } catch {
      // Best-effort — banner already dismissed visually
    }
  };

  const handleDeleteTeam = async () => {
    if (!id || !deleteTeamTarget) return;

    try {
      setDeletingTeam(true);
      await leagueApi.deleteMember(id, deleteTeamTarget.id);
      setShowDeleteTeamConfirm(false);
      setDeleteTeamTarget(null);
      setDeleteTeamConfirmName('');
      // Reload league to reflect changes
      await loadLeague(id);
    } catch (err) {
      console.error('Failed to delete team:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete team');
    } finally {
      setDeletingTeam(false);
    }
  };

  const toggleRosterView = async (memberId: string) => {
    // If already expanded, collapse it
    if (expandedMembers.has(memberId)) {
      setExpandedMembers((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
      return;
    }

    // Expand and load roster if not already loaded
    setExpandedMembers((prev) => new Set(prev).add(memberId));

    if (!memberRosters[memberId] && id) {
      setLoadingRosters((prev) => new Set(prev).add(memberId));
      try {
        const rosterData = await rosterApi.get(id, memberId);
        setMemberRosters((prev) => ({
          ...prev,
          [memberId]: rosterData,
        }));
      } catch (err) {
        console.error('Failed to load roster:', err);
        // Still remove from expanded if it failed
        setExpandedMembers((prev) => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      } finally {
        setLoadingRosters((prev) => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading league...</p>
        </div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading League</h2>
          <p className="text-gray-600 mb-4">{error || 'League not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded hover:shadow-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleRefresh = async () => {
    if (id) {
      await loadLeague(id);
      setAdRefreshKey((prev) => prev + 1);
    }
  };

  const setAutoLock = async () => {
    if (!league || !id || !rosterLockDate || !rosterLockTime) return;

    try {
      setSavingLockSettings(true);
      const lockTimeString = `${rosterLockDate}T${rosterLockTime}:00`;
      const lockTime = new Date(lockTimeString);

      if (isNaN(lockTime.getTime())) {
        alert('Invalid date/time');
        return;
      }

      if (lockTime < new Date()) {
        alert('Lock time must be in the future');
        return;
      }

      // For non-admins, enforce minimum lock time (tournament default)
      if (!userIsAdmin && league.tournamentDefaultLockTime) {
        const minLockTime = new Date(league.tournamentDefaultLockTime);
        if (lockTime < minLockTime) {
          alert(
            `Lock time cannot be earlier than the tournament start time (${formatDateTimeWithZone(league.tournamentDefaultLockTime)})`
          );
          return;
        }
      }

      await leagueApi.update(id, { rosterLockTime: lockTime.toISOString() });
      await loadLeague(id);
      setRosterLockDate('');
      setRosterLockTime('');
      alert('Auto-lock scheduled successfully');
    } catch (err) {
      console.error('Failed to set auto-lock:', err);
      alert(err instanceof Error ? err.message : 'Failed to set auto-lock');
    } finally {
      setSavingLockSettings(false);
    }
  };

  const clearAutoLock = async () => {
    if (!league || !id) return;

    try {
      setSavingLockSettings(true);
      await leagueApi.update(id, { rosterLockTime: null });
      await loadLeague(id);
    } catch (err) {
      console.error('Failed to clear auto-lock:', err);
      alert(err instanceof Error ? err.message : 'Failed to clear auto-lock');
    } finally {
      setSavingLockSettings(false);
    }
  };

  const handleAddCommissioner = async (cognitoId: string) => {
    if (!id || !league) return;
    setAddingCommissioner(true);
    try {
      const result = await leagueApi.addCommissioner(id, cognitoId);
      // Update commissioners in state directly from API response to bypass cache
      setLeague({ ...league, commissioners: result.commissioners });
    } catch (err) {
      console.error('Failed to add commissioner:', err);
      alert(err instanceof Error ? err.message : 'Failed to add commissioner');
    } finally {
      setAddingCommissioner(false);
    }
  };

  const handleRemoveCommissioner = async (cognitoId: string) => {
    if (!id || !league) return;
    setRemovingCommissioner(cognitoId);
    try {
      const result = await leagueApi.removeCommissioner(id, cognitoId);
      // Update commissioners in state directly from API response to bypass cache
      setLeague({ ...league, commissioners: result.commissioners });
    } catch (err) {
      console.error('Failed to remove commissioner:', err);
      alert(err instanceof Error ? err.message : 'Failed to remove commissioner');
    } finally {
      setRemovingCommissioner(null);
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPageNumbers = () => {
    if (!league?.pagination) return null;
    const { page, totalPages } = league.pagination;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      // Show pages around current page
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages.map((pageNum, idx) =>
      typeof pageNum === 'number' ? (
        <button
          key={idx}
          onClick={() => goToPage(pageNum)}
          className={`px-3 py-1 rounded text-sm font-medium ${
            pageNum === page
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {pageNum}
        </button>
      ) : (
        <span key={idx} className="px-2 text-gray-400">
          {pageNum}
        </span>
      )
    );
  };

  return (
    <MobileLayout>
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          {/* League Name - Editable for Creator */}
          {editingLeagueName && isCommissioner() ? (
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={editLeagueName}
                onChange={(e) => setEditLeagueName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveLeagueName();
                  if (e.key === 'Escape') cancelEditLeagueName();
                }}
                maxLength={200}
                className="flex-1 px-2 py-1 border border-purple-500 rounded text-xl font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                autoFocus
              />
              <button
                onClick={saveLeagueName}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex-shrink-0"
              >
                Save
              </button>
              <button
                onClick={cancelEditLeagueName}
                className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 flex-shrink-0"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="mb-2">
              {/* Single line header with breadcrumb, centered name, and actions */}
              <div className="flex items-center justify-between gap-2">
                {/* Left: Breadcrumb */}
                <button
                  onClick={() => navigate('/leagues')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  <span>Leagues</span>
                </button>

                {/* Center: League name */}
                <h1
                  onClick={() => {
                    if (isCommissioner()) startEditLeagueName();
                  }}
                  className={`text-xl font-bold text-gray-900 ${isCommissioner() ? 'cursor-pointer hover:text-purple-600' : ''}`}
                >
                  {league.name}
                </h1>

                {/* Right: Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
                    aria-label="Refresh"
                  >
                    <svg
                      className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {league.tournamentFinalized && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-green-900">Results are final</div>
                  <div className="text-sm text-green-700 mt-1">
                    Official standings have been confirmed.
                    {league.scoresLastUpdated && (
                      <div className="text-xs text-green-600 mt-1">
                        Scores updated {formatDateTimeWithZone(league.scoresLastUpdated)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {league.rosterLocked && !league.tournamentFinalized && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">🔒</div>
                <div className="flex-1">
                  <div className="font-medium text-green-900">Rosters are locked!</div>
                  <div className="text-sm text-green-700 mt-1">
                    The action has begun. Check the standings and follow along!
                    {league.scoresLastUpdated && (
                      <div className="text-xs text-green-600 mt-1">
                        Scores updated {formatDateTimeWithZone(league.scoresLastUpdated)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!league.rosterLocked && league.rosterLockTime && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">⏰</div>
                <div className="flex-1">
                  <div className="font-medium text-yellow-900">Rosters will lock automatically</div>
                  <div className="text-sm text-yellow-700 mt-1">
                    Lock time: {formatDateTimeWithZone(league.rosterLockTime)}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!league.rosterLocked && !league.rosterLockTime && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">ℹ️</div>
                <div className="flex-1">
                  <div className="font-medium text-blue-900">No roster lock time set</div>
                  <div className="text-sm text-blue-700 mt-1">
                    Commissioners can set a lock time in league settings.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(league.tournamentFloUrl || league.tournamentWrestlestatUrl) && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap justify-center gap-3">
            {league.tournamentFloUrl && (
              <a
                href={league.tournamentFloUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-sm transition-all"
              >
                Follow Brackets
              </a>
            )}
            {league.tournamentWrestlestatUrl && (
              <a
                href={league.tournamentWrestlestatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm transition-all"
              >
                Wrestler Stats
              </a>
            )}
          </div>
        </div>
      )}

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-2 space-y-4">
          <div className="py-1">
            <AdBanner slot={AD_SLOTS.leagueDetails} format="horizontal" refreshKey={adRefreshKey} />
          </div>

          {/* Pagination Controls - Top */}
          {league.pagination && league.pagination.totalPages > 1 && (
            <div className="bg-white shadow-sm rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {league.pagination.page === 1
                    ? 1
                    : (league.pagination.page - 1) * league.pagination.pageSize + 1}{' '}
                  to{' '}
                  {Math.min(
                    league.pagination.page * league.pagination.pageSize,
                    league.pagination.totalItems
                  )}{' '}
                  of {league.pagination.totalItems} teams
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1 flex-wrap justify-center">
                  {renderPageNumbers()}
                </div>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === league.pagination.totalPages}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Commissioner message banners for unread messages */}
          {inboxMessages
            .filter((m) => !m.readAt && !dismissedMessageIds.has(m.id))
            .map((m) => (
              <div
                key={m.id}
                className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-blue-900">
                    Message from your commissioner
                  </div>
                  <div className="text-sm text-blue-800 mt-0.5 break-words">{m.content}</div>
                </div>
                <button
                  onClick={() => handleDismissMessage(m.id)}
                  className="flex-shrink-0 text-blue-400 hover:text-blue-600 transition-colors"
                  aria-label="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}

          {/* Previous (read/dismissed) commissioner messages */}
          {(() => {
            const readMessages = inboxMessages.filter(
              (m) => m.readAt || dismissedMessageIds.has(m.id)
            );
            if (readMessages.length === 0) return null;
            return (
              <div>
                <button
                  onClick={() => setShowReadMessages((v) => !v)}
                  className="text-xs text-blue-500 hover:text-blue-700 underline"
                >
                  {showReadMessages
                    ? 'Hide previous commissioner messages'
                    : `Show ${readMessages.length} previous commissioner message${readMessages.length > 1 ? 's' : ''}`}
                </button>
                {showReadMessages && (
                  <div className="mt-2 space-y-2">
                    {readMessages.map((m) => (
                      <div
                        key={m.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-500">
                            Message from your commissioner
                          </div>
                          <div className="text-sm text-gray-700 mt-0.5 break-words">
                            {m.content}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Members List */}
          <div className="bg-white shadow-sm rounded-lg p-4">
            {league.members.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No members yet. Invite friends to get started!
              </p>
            ) : (
              <div className="space-y-3">
                {league.members.map((member) => {
                  const isExpanded = expandedMembers.has(member.id);
                  const roster = memberRosters[member.id];
                  const isLoadingRoster = loadingRosters.has(member.id);

                  return (
                    <div
                      key={member.id}
                      className={`border rounded-lg overflow-hidden ${
                        isUserMember(member.cognitoId)
                          ? isExpanded
                            ? 'bg-purple-100 border-purple-400'
                            : 'bg-purple-50 border-purple-300'
                          : isExpanded
                            ? 'bg-purple-50 border-purple-300'
                            : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Editing Mode - Full width input */}
                      {editingMemberId === member.id ? (
                        <div className="p-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editTeamName}
                              onChange={(e) => setEditTeamName(e.target.value)}
                              maxLength={200}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded text-base focus:ring-purple-500 focus:border-purple-500"
                              placeholder="Team name"
                              autoFocus
                            />
                            <button
                              onClick={() => saveTeamName(member.id)}
                              className="text-green-600 hover:text-green-700 text-sm font-medium px-2"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditTeamName}
                              className="text-gray-600 hover:text-gray-700 text-sm font-medium px-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Single Line Display */
                        <div
                          onClick={(e) => {
                            // If clicking on team name and it's the user's team, start editing
                            const target = e.target as HTMLElement;
                            if (
                              target.closest('.team-name-area') &&
                              isUserMember(member.cognitoId)
                            ) {
                              startEditTeamName(member.id, member.teamName);
                            } else if (league.tournamentId) {
                              // Otherwise toggle roster
                              toggleRosterView(member.id);
                            }
                          }}
                          className="p-3 cursor-pointer hover:bg-opacity-80 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            {/* Left side: Rank + Points + Team Name */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {/* Rank and Points */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {member.rank ? (
                                  <span className="text-sm font-bold text-gray-900">
                                    #{member.rank}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                )}
                                <span className="text-sm text-gray-600">
                                  {formatPoints(league.rosterLocked ? member.totalPoints : 0)} pts
                                </span>
                              </div>

                              {/* Team Name - editable area */}
                              <div className="team-name-area flex-1 min-w-0 flex flex-col">
                                <span className="font-semibold text-gray-900 text-sm truncate">
                                  {member.teamName || (
                                    <span className="italic text-gray-400 text-xs">
                                      {isUserMember(member.cognitoId)
                                        ? 'Tap to set name'
                                        : 'No name'}
                                    </span>
                                  )}
                                </span>
                                {member.status === 'pending' && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded self-start">
                                    Pending Commish. Approval
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Right side: Member Name + Envelope + Arrow */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-gray-600 truncate max-w-[120px]">
                                {member.displayName ||
                                  member.email ||
                                  `User #${member.userId.substring(0, 8)}`}
                              </span>
                              {isCommissioner() && !isUserMember(member.cognitoId) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMessageModalTarget({
                                      memberId: member.id,
                                      name:
                                        member.teamName ||
                                        member.displayName ||
                                        member.email ||
                                        'Member',
                                    });
                                  }}
                                  className="p-1 text-gray-400 hover:text-purple-600 transition-colors flex-shrink-0"
                                  aria-label="Send message"
                                  title="Send message to this member"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              )}
                              {league.tournamentId && (
                                <span className="text-gray-400 text-sm">
                                  {isLoadingRoster ? '⏳' : isExpanded ? '▲' : '▼'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Commissioner Approve/Reject for pending members */}
                      {member.status === 'pending' && isCommissioner() && (
                        <div className="px-3 pb-3 flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveMember(member.id);
                            }}
                            disabled={approvingMember === member.id}
                            className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {approvingMember === member.id ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectMember(member.id, member.teamName);
                            }}
                            disabled={rejectingMember === member.id}
                            className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                          >
                            {rejectingMember === member.id ? 'Rejecting...' : 'Reject'}
                          </button>
                        </div>
                      )}

                      {/* Expanded Roster View */}
                      {isExpanded && roster && (
                        <div className="mt-3 pt-3 border-t border-gray-300 space-y-2">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                            <span>
                              Budget:{' '}
                              <span className="font-semibold">
                                ${roster.totalCost}/${league.budgetPerTeam}
                              </span>{' '}
                              used
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>
                              Wrestlers:{' '}
                              <span className="font-semibold">{roster.wrestlers.length}</span>
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>
                              Points:{' '}
                              <span className="font-semibold text-green-600">
                                {league.rosterLocked ? roster.totalPoints : 0}
                              </span>
                            </span>
                          </div>

                          {/* Show full roster details only if rosters are locked OR it's the user's own roster */}
                          {league.rosterLocked || isUserMember(member.cognitoId) ? (
                            roster.wrestlers.length === 0 ? (
                              <p className="text-center text-gray-500 py-2 text-xs italic">
                                No wrestlers selected yet
                              </p>
                            ) : (
                              <div className="space-y-1.5">
                                {roster.wrestlers.map((wrestler) => (
                                  <div
                                    key={wrestler.id}
                                    className="bg-white border border-gray-200 rounded px-2 py-1.5"
                                  >
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className="font-medium text-gray-700 flex-shrink-0">
                                        {wrestler.weightClass}
                                      </span>
                                      <span className="text-gray-400">|</span>
                                      {wrestler.wrestlerSeed && (
                                        <>
                                          <span className="text-blue-600 font-medium flex-shrink-0">
                                            Seed {wrestler.wrestlerSeed}
                                          </span>
                                          <span className="text-gray-400">|</span>
                                        </>
                                      )}
                                      <span className="font-semibold text-gray-900 truncate">
                                        {wrestler.wrestlerName}
                                      </span>
                                      <span className="text-gray-400">|</span>
                                      <span className="text-gray-600 truncate">
                                        {wrestler.wrestlerSchoolTeam || 'Unknown'}
                                      </span>
                                      <span className="text-gray-400">|</span>
                                      <span className="text-green-600 font-semibold flex-shrink-0 ml-auto">
                                        {league.rosterLocked ? wrestler.wrestlerTeamPoints : 0} pts
                                      </span>
                                      {league.rosterLocked &&
                                        wrestler.wrestlerMatchResults &&
                                        wrestler.wrestlerMatchResults.matches.length > 0 && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setScoringDetailWrestler(wrestler);
                                            }}
                                            className="text-gray-400 hover:text-purple-600 transition-colors flex-shrink-0"
                                            title="View scoring breakdown"
                                          >
                                            <svg
                                              className="w-3.5 h-3.5"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                              strokeWidth={2}
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            /* Hidden roster message when locked and not the user's team */
                            <div className="py-4 text-center">
                              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                                <svg
                                  className="w-4 h-4 text-gray-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                <span className="text-xs text-gray-600 font-medium">
                                  Roster hidden until lock
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Manage Roster Button - only shows in expanded view for your own team */}
                          {isUserMember(member.cognitoId) &&
                            editingMemberId !== member.id &&
                            (league.rosterLocked ? (
                              <div className="w-full mt-3 py-3 bg-gray-100 border border-gray-300 rounded-lg text-center">
                                <div className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600">
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                  <span>Roster Locked</span>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  navigate(`/leagues/${league.id}/members/${member.id}/roster`)
                                }
                                className="w-full mt-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 shadow-sm"
                              >
                                Manage Roster
                              </button>
                            ))}

                          {/* Delete Team - tucked inside expanded view */}
                          {(isCommissioner() || isUserMember(member.cognitoId)) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTeamTarget({ id: member.id, teamName: member.teamName });
                                setShowDeleteTeamConfirm(true);
                              }}
                              className="w-full mt-3 px-3 py-1.5 text-gray-400 text-xs hover:text-red-600 transition-colors"
                            >
                              Delete Team
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Share prompts based on standing / league status */}
          {(() => {
            const userTeams = league.currentUserTeams?.filter((t) => t.status === 'approved') ?? [];
            if (userTeams.length === 0 || !league.rosterLocked) return null;

            // Find the user's best rank
            const bestRank = userTeams.reduce(
              (min, t) => (t.rank !== null && t.rank < min ? t.rank : min),
              Infinity
            );

            if (league.status === 'completed') {
              // Final results share
              const rankLabel =
                bestRank === 1
                  ? '1st'
                  : bestRank === 2
                    ? '2nd'
                    : bestRank === 3
                      ? '3rd'
                      : `${bestRank}th`;
              return (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">🏆</div>
                    <div className="flex-1">
                      <div className="font-medium text-purple-900 mb-1">
                        Tournament over — share your result!
                      </div>
                      <ShareButton
                        text={`I finished ${rankLabel} in my ${league.tournamentName ?? 'wrestling'} league on #TakedownLeague! Already planning next season. #NCAAWrestling #MarchMATness`}
                        hashtags={league.tournamentHashtags}
                        label="Share result"
                      />
                    </div>
                  </div>
                </div>
              );
            }

            if (bestRank === 1) {
              return (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">🥇</div>
                    <div className="flex-1">
                      <div className="font-medium text-yellow-900 mb-1">
                        You're in 1st place — brag a little!
                      </div>
                      <ShareButton
                        text={`I'm sitting at #1 in my ${league.tournamentName ?? 'wrestling'} league on #TakedownLeague! My picks are paying off. #NCAAWrestling #MarchMATness`}
                        hashtags={league.tournamentHashtags}
                        label="Share your lead"
                      />
                    </div>
                  </div>
                </div>
              );
            }

            return null;
          })()}

          {/* Pagination Controls - Bottom */}
          {league.pagination && league.pagination.totalPages > 1 && (
            <div className="bg-white shadow-sm rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1 flex-wrap justify-center">
                  {renderPageNumbers()}
                </div>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === league.pagination.totalPages}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Commissioner Info */}
          {league.commissioners && league.commissioners.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-600">
                <span className="font-medium">
                  Commissioner{league.commissioners.length > 1 ? 's' : ''}:
                </span>{' '}
                <span>{league.commissioners.map((c) => c.displayName || c.email).join(', ')}</span>
              </div>
            </div>
          )}

          {/* Expandable Invite Section */}
          {league.inviteCode && (
            <div className="mt-4">
              {/* Toggle Button */}
              <button
                onClick={() => setShowInvite(!showInvite)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  <span>Invite Friends</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${showInvite ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Expanded Content */}
              {showInvite && (
                <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg space-y-3">
                  {/* Share on social */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      Share on social:
                    </label>
                    <ShareButton
                      text={`Join my ${league.tournamentName ?? 'fantasy wrestling'} league on #TakedownLeague! #NCAAWrestling #MarchMATness`}
                      hashtags={league.tournamentHashtags}
                      url={`${window.location.origin}/join/${league.inviteCode}`}
                      label="Invite Friends"
                    />
                  </div>

                  {/* Full Link */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Full Link:
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded px-3 py-2 text-xs font-mono text-gray-700 truncate border border-gray-200">
                        {window.location.origin}/join/{league.inviteCode}
                      </div>
                      <button
                        onClick={copyInviteLink}
                        className="px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded text-xs font-medium hover:shadow-lg transition-colors flex-shrink-0"
                      >
                        {copied ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* Code Only */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Code Only:
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded px-3 py-2 text-sm font-mono font-semibold text-gray-900 border border-gray-200">
                        {league.inviteCode}
                      </div>
                      <button
                        onClick={copyInviteCode}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 transition-colors flex-shrink-0"
                      >
                        {copiedCode ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* QR Code */}
                  <div>
                    <button
                      onClick={() => setShowQR(true)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5V16M4 4h4v4H4V4zm12 0h4v4h-4V4zM4 16h4v4H4v-4z"
                        />
                      </svg>
                      Share via QR Code
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* League Settings (Commissioner Only) */}
          {isCommissioner() && (
            <div className="mt-4">
              {/* Toggle Button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>League Settings</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Expanded Settings Content */}
              {showSettings && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {/* Budget Settings */}
                  <div className="px-3 py-2.5">
                    {editingBudget ? (
                      <div className="flex items-center gap-2">
                        <span className="text-lg">💰</span>
                        <span className="text-sm text-gray-600">Budget:</span>
                        <span className="text-sm">$</span>
                        <input
                          type="number"
                          value={editBudget}
                          onChange={(e) => setEditBudget(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveBudget();
                            if (e.key === 'Escape') cancelEditBudget();
                          }}
                          min="1"
                          className="w-16 px-2 py-1 border border-purple-500 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                          autoFocus
                        />
                        <span className="text-sm text-gray-600">/team</span>
                        <div className="flex gap-1 ml-auto">
                          <button
                            onClick={saveBudget}
                            className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditBudget}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">💰</span>
                          <span className="text-sm text-gray-600">Budget:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            ${league.budgetPerTeam}/team
                          </span>
                        </div>
                        <button
                          onClick={startEditBudget}
                          className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Roster Lock Settings */}
                  <div className="px-3 py-2.5">
                    {league.rosterLocked ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🔒</span>
                            <span className="text-sm text-gray-600">Rosters:</span>
                            <span className="text-sm font-semibold text-gray-900">Locked</span>
                          </div>
                          {userIsAdmin && (
                            <button
                              onClick={async () => {
                                if (!id) return;
                                try {
                                  setSavingLockSettings(true);
                                  await leagueApi.update(id, { rosterLocked: false });
                                  await loadLeague(id);
                                  setTimeout(() => {
                                    fetchUnreadCount();
                                  }, 1000);
                                } catch (err) {
                                  console.error('Failed to unlock rosters:', err);
                                  alert(
                                    err instanceof Error ? err.message : 'Failed to unlock rosters'
                                  );
                                } finally {
                                  setSavingLockSettings(false);
                                }
                              }}
                              disabled={savingLockSettings}
                              className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                            >
                              {savingLockSettings ? 'Saving...' : 'Unlock (Admin)'}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">⏰</span>
                          <span className="text-sm text-gray-600">Lock Time:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {league.rosterLockTime
                              ? formatDateTimeWithZone(league.rosterLockTime)
                              : 'Not Set'}
                          </span>
                        </div>
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">
                            Set new lock time ({getUserTimezone()}):
                            {!userIsAdmin && league.tournamentDefaultLockTime && (
                              <span className="block mt-1 text-orange-600">
                                Minimum: {formatDateTimeWithZone(league.tournamentDefaultLockTime)}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={rosterLockDate}
                              onChange={(e) => setRosterLockDate(e.target.value)}
                              min={
                                !userIsAdmin && league.tournamentDefaultLockTime
                                  ? new Date(league.tournamentDefaultLockTime)
                                      .toISOString()
                                      .split('T')[0]
                                  : undefined
                              }
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                              disabled={savingLockSettings}
                            />
                            <input
                              type="time"
                              value={rosterLockTime}
                              onChange={(e) => setRosterLockTime(e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                              disabled={savingLockSettings}
                            />
                          </div>
                          <button
                            onClick={setAutoLock}
                            disabled={savingLockSettings || !rosterLockDate || !rosterLockTime}
                            className="w-full px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                          >
                            {savingLockSettings ? 'Saving...' : 'Set Lock Time'}
                          </button>
                          {league.rosterLockTime && userIsAdmin && (
                            <button
                              onClick={clearAutoLock}
                              disabled={savingLockSettings}
                              className="w-full px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 disabled:opacity-50"
                            >
                              Clear Lock Time
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Team Approval Toggle */}
                  <div className="px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">✓</span>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            Require Approval for New Teams
                          </div>
                          <div className="text-xs text-gray-500">
                            New teams must be approved before appearing in standings
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleApprovalRequired(!requireTeamApproval)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          requireTeamApproval ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            requireTeamApproval ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Allow Multiple Entries Toggle */}
                  <div className="px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">#</span>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            Allow Multiple Entries Per User
                          </div>
                          <div className="text-xs text-gray-500">
                            Users can create more than one team in this league
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleAllowMultipleEntries(!allowMultipleEntries)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          allowMultipleEntries ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            allowMultipleEntries ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Manage Commissioners */}
                  <div className="px-3 py-2.5">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">👥</span>
                        <span className="text-sm font-semibold text-gray-900">
                          Manage Commissioners
                        </span>
                      </div>

                      {/* Current Commissioners */}
                      <div className="space-y-1.5">
                        <div className="text-xs text-gray-600 font-medium">
                          Current Commissioners:
                        </div>
                        {league.commissioners?.map((commissioner) => (
                          <div
                            key={commissioner.cognitoId}
                            className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                          >
                            <div className="flex items-center gap-2 text-xs">
                              <span className="font-medium text-gray-900">
                                {commissioner.displayName || commissioner.email}
                              </span>
                              {commissioner.cognitoId === user?.username && (
                                <span className="text-purple-600 font-medium">(You)</span>
                              )}
                            </div>
                            {commissioner.cognitoId !== user?.username && (
                              <button
                                onClick={() => handleRemoveCommissioner(commissioner.cognitoId)}
                                disabled={removingCommissioner === commissioner.cognitoId}
                                className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 disabled:opacity-50 transition-colors"
                              >
                                {removingCommissioner === commissioner.cognitoId
                                  ? 'Removing...'
                                  : 'Remove'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add Commissioner */}
                      <div className="space-y-1.5 pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-600 font-medium">Add Commissioner:</div>
                        <div className="space-y-1">
                          {(() => {
                            // Deduplicate members by cognitoId (users can have multiple teams)
                            const uniqueUsers = Array.from(
                              new Map(
                                league.members
                                  .filter((member) => member.cognitoId)
                                  .map((member) => [member.cognitoId, member])
                              ).values()
                            ).filter(
                              (member) =>
                                !league.commissioners?.some((c) => c.cognitoId === member.cognitoId)
                            );

                            if (uniqueUsers.length === 0) {
                              return (
                                <div className="text-xs text-gray-500 italic">
                                  All league members are already commissioners
                                </div>
                              );
                            }

                            return uniqueUsers.map((member) => (
                              <div
                                key={member.cognitoId}
                                className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                              >
                                <div className="text-xs">
                                  <span className="font-medium text-gray-900">
                                    {member.displayName || member.email}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleAddCommissioner(member.cognitoId!)}
                                  disabled={addingCommissioner}
                                  className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 disabled:opacity-50 transition-colors"
                                >
                                  {addingCommissioner ? 'Adding...' : 'Add'}
                                </button>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Delete League Link - Only for Commissioner */}
          {isCommissioner() && (
            <div className="text-center py-8">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-xs text-gray-400 hover:text-red-600 underline"
              >
                Delete league
              </button>
            </div>
          )}
        </div>
      </PullToRefresh>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete League?</h2>
            <p className="text-gray-600 mb-4">
              This will permanently delete <span className="font-semibold">{league.name}</span> and
              all member data. This action cannot be undone.
            </p>
            <p className="text-sm text-gray-600 mb-2">Type the league name to confirm:</p>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={league.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base mb-4 focus:ring-red-500 focus:border-red-500"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleDeleteLeague}
                disabled={deleting || deleteConfirmName !== league.name}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete League'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmName('');
                }}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Team Confirmation Modal */}
      {showDeleteTeamConfirm && deleteTeamTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Team?</h2>
            <p className="text-gray-600 mb-4">
              This will permanently delete{' '}
              <span className="font-semibold">{deleteTeamTarget.teamName || 'this team'}</span> and
              all roster picks. This action cannot be undone.
            </p>
            <p className="text-sm text-gray-600 mb-2">Type the team name to confirm:</p>
            <input
              type="text"
              value={deleteTeamConfirmName}
              onChange={(e) => setDeleteTeamConfirmName(e.target.value)}
              placeholder={deleteTeamTarget.teamName || 'team name'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base mb-4 focus:ring-red-500 focus:border-red-500"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleDeleteTeam}
                disabled={
                  deletingTeam || deleteTeamConfirmName !== (deleteTeamTarget.teamName || '')
                }
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingTeam ? 'Deleting...' : 'Delete Team'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteTeamConfirm(false);
                  setDeleteTeamTarget(null);
                  setDeleteTeamConfirmName('');
                }}
                disabled={deletingTeam}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Button - constrained to phone column */}
      <div className="fixed bottom-20 left-0 right-0 max-w-md mx-auto z-40 pointer-events-none">
        <div className="flex justify-end pr-4">
          <button
            onClick={() => {
              setShowChat(true);
              resetUnreadCount();
            }}
            className="relative w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95 flex items-center justify-center pointer-events-auto"
            aria-label="Open chat"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            {/* Unread badge */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-lg">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Chat Slideout */}
      <ChatSlideout
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        leagueId={id!}
        leagueName={league.name}
      />

      {/* Commissioner Message Modal */}
      {messageModalTarget && (
        <CommissionerMessageModal
          isOpen={true}
          onClose={() => setMessageModalTarget(null)}
          leagueId={id!}
          recipientMemberId={messageModalTarget.memberId}
          recipientName={messageModalTarget.name}
        />
      )}

      {/* Scoring Details Modal */}
      {scoringDetailWrestler && scoringDetailWrestler.wrestlerMatchResults && (
        <ScoringDetailsModal
          wrestler={scoringDetailWrestler}
          onClose={() => setScoringDetailWrestler(null)}
        />
      )}

      {/* QR Code Full-Screen Modal */}
      {showQR && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white"
          onClick={() => setShowQR(false)}
        >
          <button
            onClick={() => setShowQR(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div
            className="flex flex-col items-center gap-6 px-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 text-center">{league.name}</h2>
            <QRCode
              value={`${window.location.origin}/join/${league.inviteCode}`}
              size={280}
              style={{ height: 'auto', maxWidth: '100%', width: '280px' }}
            />
            <p className="text-base text-gray-500 text-center">Scan to join this league</p>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}
